<script setup lang="ts">
import { useId } from 'vue'
import { useI18n } from 'vue-i18n'

withDefaults(defineProps<{ withTagline?: boolean }>(), { withTagline: false })

const { t } = useI18n()

// Unique per instance: SVG ids are document-global, and this component can
// render more than once at a time (header + auth screens).
const uid = useId()
const waxGradientId = `travick-wax-${uid}`
</script>

<template>
  <span class="flex items-center gap-2.5">
    <!-- A wax seal with a serif T pressed into it: the alliance's orders,
         stamped and closed. The red is the only warm colour on the blue
         chrome, so the eye lands on it first. Kept to one silhouette and one
         letter so it still reads at favicon size (public/favicon.svg is the
         same drawing). Inline SVG so it costs no extra request and takes its
         colours from the theme tokens. -->
    <svg class="h-9 w-9 shrink-0" viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <defs>
        <radialGradient :id="waxGradientId" cx="0.36" cy="0.3" r="0.8">
          <stop offset="0" stop-color="var(--color-wax-400)" />
          <stop offset="0.5" stop-color="var(--color-wax-600)" />
          <stop offset="1" stop-color="var(--color-wax-800)" />
        </radialGradient>
      </defs>

      <g style="filter: drop-shadow(0 1.6px 1.8px rgba(0, 0, 0, 0.55))">
        <!-- uneven rim: poured wax, not a machined disc -->
        <path
          d="M59.5 32L60.8 33.9L60.7 35.8L59.3 37.4L57.6 38.9L56.6 40.3L56.7 42.2L57.1 44.4L56.7 46.3L55 47.4L52.7 47.9L50.9 48.6L50 50L49.6 52.1L48.9 54L47.4 55L45.2 54.9L43.2 54.7L41.7 55.4L40.5 57.1L39.3 59.1L37.6 60L35.6 59.6L33.7 58.5L32 58L30.3 58.7L28.3 59.9L26.3 60.5L24.6 59.6L23.3 57.7L22.1 56L20.5 55.3L18.5 55.4L16.4 55.4L14.9 54.3L14.3 52.2L14 50L13.2 48.5L11.5 47.7L9.3 47.1L7.8 46L7.4 44.1L7.9 42L8 40.1L7 38.7L5.1 37.3L3.6 35.7L3.4 33.9L4.5 32L5.7 30.3L6 28.6L5.2 26.7L4.4 24.6L4.6 22.7L6.2 21.3L8.4 20.3L9.9 19.2L10.3 17.5L10.3 15.4L10.9 13.5L12.5 12.5L14.8 12.4L16.8 12.2L18 11.1L18.8 9.1L19.7 7.1L21.3 6.1L23.3 6.3L25.3 7L27 6.8L28.5 5.5L30.2 3.8L32 3L33.9 3.7L35.5 5.2L37.1 6.4L38.9 6.4L40.9 5.7L43 5.5L44.6 6.5L45.5 8.6L46.2 10.7L47.4 11.9L49.3 12.2L51.5 12.5L53 13.6L53.4 15.6L53.3 17.8L53.6 19.5L55.1 20.6L57.2 21.6L58.8 22.9L59.1 24.7L58.3 26.8L57.7 28.6L58.1 30.3Z"
          :fill="`url(#${waxGradientId})`"
        />
      </g>

      <!-- pressed ring: dark edge top-left, catch-light bottom-right -->
      <circle
        cx="32.5"
        cy="32.7"
        r="19.6"
        stroke="var(--color-wax-300)"
        stroke-opacity="0.35"
        stroke-width="1.3"
      />
      <circle
        cx="32"
        cy="32"
        r="19.6"
        stroke="var(--color-wax-900)"
        stroke-opacity="0.75"
        stroke-width="1.3"
      />

      <!-- the T, debossed the same way -->
      <path
        d="M19.5 20.5H44.5V27.6L42.3 24.6H35.4V42.4L38.6 44.6V45.8H25.4V44.6L28.6 42.4V24.6H21.7L19.5 27.6Z"
        transform="translate(0.6 0.8)"
        fill="var(--color-wax-300)"
        opacity="0.42"
      />
      <path
        d="M19.5 20.5H44.5V27.6L42.3 24.6H35.4V42.4L38.6 44.6V45.8H25.4V44.6L28.6 42.4V24.6H21.7L19.5 27.6Z"
        fill="var(--color-wax-900)"
      />

      <!-- gloss on the wax -->
      <ellipse
        cx="22"
        cy="16"
        rx="7"
        ry="3.2"
        transform="rotate(-35 22 16)"
        fill="#fff"
        opacity="0.16"
      />
    </svg>

    <span class="flex flex-col leading-none">
      <span class="font-display text-parchment-50 text-xl font-bold tracking-widest">
        {{ t('brand.name') }}
      </span>
      <span v-if="withTagline" class="text-gold-300 mt-0.5 text-[0.65rem] tracking-wide uppercase">
        {{ t('brand.tagline') }}
      </span>
    </span>
  </span>
</template>
